import AppDialog from '@/common/components/Dialog.tsx'
import {
  InviteProps,
  UserInviteFormValues,
  UserInviteSchema,
} from '@/pages/providers/Invite/types.ts'
import { Input } from '@/app/common/components/input/Input'
import { Button } from '@/app/common/components/button/Button'
import { PulsingEllipsesLoader } from '@/common/components/PulsingEllipsesLoader.tsx'
import { useForm, SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { sendInviteProvider } from '@/apis/Providers/requests.ts'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'

const InviteProvider = ({
  inviteModalVisible,
  setInviteModalVisible,
  inviteIsFetching,
  selectedProgramYear,
}: InviteProps) => {
  const { fireAlert } = useAppAlert()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserInviteFormValues>({
    resolver: zodResolver(UserInviteSchema),
  })

  const onSubmit: SubmitHandler<UserInviteFormValues> = async (data) => {
    const payload = {
      program_id: selectedProgramYear ?? 0,
      email: data.email ?? '',
      name: data.name,
    }

    try {
      const response = await sendInviteProvider(payload)
      if (response.error) {
        console.error('Invite Error:', response.error)

        await fireAlert({
          variant: 'error',
          message: response.error.message || 'An unexpected error occurred.',
        })

        return
      }

      await fireAlert({
        variant: 'success',
        message: 'Invitation sent successfully.',
      })

      setInviteModalVisible(false)
      resetForm()
    } catch (err) {
      console.error('An unexpected error occurred:', err)

      await fireAlert({
        variant: 'error',
        message: 'An unexpected error occurred. Please try again later.',
      })
    }
  }

  const resetForm = () => {
    reset({
      name: '',
      email: '',
    })
  }

  return (
    <AppDialog open={inviteModalVisible} setOpen={setInviteModalVisible}>
      <AppDialog.Title>Invite Provider</AppDialog.Title>
      <div className="flex flex-col mt-6">
        <form id="invite_applicant_form" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Name
            </label>
            <div className="mt-2">
              <Input id="name" {...register('name')} name="name" type="text" />
              {errors.name && (
                <span className="text-sm text-red-600">
                  {errors.name.message}
                </span>
              )}
            </div>
            <div className="mt-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Email
              </label>
              <Input
                id="email"
                {...register('email')}
                name="email"
                type="text"
              />
              {errors.email && (
                <span className="text-sm text-red-600">
                  {errors.email.message}
                </span>
              )}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-end gap-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setInviteModalVisible(false)
                resetForm()
              }}
              type="reset"
              className="h-9 w-32 disabled:hover:bg-white disabled:opacity-70"
              disabled={inviteIsFetching}
              form="invite_applicant_form"
            >
              Cancel
            </Button>
            <Button
              form="invite_applicant_form"
              type="submit"
              className="h-9 w-32 disabled:hover:bg-primary disabled:opacity-70"
              disabled={inviteIsFetching}
            >
              {inviteIsFetching ? (
                <PulsingEllipsesLoader size="md" color="gray" />
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </form>
      </div>
    </AppDialog>
  )
}
export default InviteProvider
